import { z } from "zod";

import type { PortableBoardSnapshotV1 } from "./portable-snapshot";

export const restoreAttachmentStatusSchema = z.enum([
  "pending",
  "copying",
  "copied",
  "link_only",
  "skipped",
  "failed",
]);
export type RestoreAttachmentStatus = z.infer<
  typeof restoreAttachmentStatusSchema
>;

export const restorePreviewRequestSchema = z.object({
  snapshotId: z.uuid(),
});
export type RestorePreviewRequest = z.infer<typeof restorePreviewRequestSchema>;

export const selectiveRestoreScopeSchema = z.enum(["list", "card"]);
export type SelectiveRestoreScope = z.infer<typeof selectiveRestoreScopeSchema>;

export const selectiveRestoreCandidatesSchema = z.object({
  cards: z.array(
    z.object({
      id: z.string().min(1).max(512),
      listId: z.string().min(1).max(512),
      name: z.string().min(1),
    }),
  ),
  lists: z.array(
    z.object({
      id: z.string().min(1).max(512),
      name: z.string().min(1),
    }),
  ),
  snapshotId: z.uuid(),
});
export type SelectiveRestoreCandidates = z.infer<
  typeof selectiveRestoreCandidatesSchema
>;

export const selectiveRestorePreviewRequestSchema = z.object({
  scope: selectiveRestoreScopeSchema,
  selectedSourceId: z.string().min(1).max(512),
  snapshotId: z.uuid(),
});
export type SelectiveRestorePreviewRequest = z.infer<
  typeof selectiveRestorePreviewRequestSchema
>;

export const restoreStepTypeSchema = z.enum([
  "board",
  "list",
  "label",
  "custom_field",
  "card",
  "member_assignment",
  "checklist",
  "check_item",
  "attachment_file",
  "attachment_link",
  "card_cover",
  "historical_comment",
  "historical_activity",
]);
export type RestoreStepType = z.infer<typeof restoreStepTypeSchema>;

export const restoreFidelitySchema = z.enum(["supported", "best_effort"]);
export type RestoreFidelity = z.infer<typeof restoreFidelitySchema>;

export const restoreExecutionOperationSchema = z.object({
  fidelity: restoreFidelitySchema,
  operationType: restoreStepTypeSchema,
  parentSourceId: z.string().min(1).max(512).nullable(),
  sequence: z.int().positive(),
  sourceId: z.string().min(1).max(512),
});
export type RestoreExecutionOperation = z.infer<
  typeof restoreExecutionOperationSchema
>;

export const restoreWarningCodeSchema = z.enum([
  "ACTIVITY_ARCHIVE_ONLY",
  "ARCHIVED_OBJECTS_RECREATED",
  "ATTACHMENTS_UNAVAILABLE",
  "BOARD_BACKGROUND_NOT_RESTORED",
  "CARD_COVERS_BEST_EFFORT",
  "COMMENTS_ARCHIVE_ONLY",
  "CUSTOM_FIELDS_BEST_EFFORT",
  "MEMBERS_REQUIRE_MAPPING",
  "MEMBERSHIPS_BEST_EFFORT",
  "HISTORICAL_RECORDS_BEST_EFFORT",
  "ORIGINAL_IDENTITY_NOT_PRESERVED",
  "PERSONAL_STATE_NOT_RESTORED",
]);
export type RestoreWarningCode = z.infer<typeof restoreWarningCodeSchema>;

export const restorePlanStepSchema = z.object({
  count: z.int().nonnegative(),
  fidelity: restoreFidelitySchema,
  order: z.int().positive(),
  type: restoreStepTypeSchema,
});
export type RestorePlanStep = z.infer<typeof restorePlanStepSchema>;

export const restorePlanWarningSchema = z.object({
  code: restoreWarningCodeSchema,
  count: z.int().positive(),
  message: z.string().min(1).max(500),
});
export type RestorePlanWarning = z.infer<typeof restorePlanWarningSchema>;

export const restorePreviewDraftSchema = z.object({
  archiveOnly: z.object({
    activities: z.int().nonnegative(),
    comments: z.int().nonnegative(),
  }),
  destination: z.object({
    kind: z.literal("new_private_board"),
    permissionLevel: z.literal("private"),
  }),
  safety: z.object({
    confirmed: z.literal(false),
    createsOnly: z.literal(true),
    dryRun: z.literal(true),
    sourceUnchanged: z.literal(true),
    trelloWrites: z.literal(0),
  }),
  schemaVersion: z.literal(1),
  snapshot: z.object({
    boardName: z.string().min(1),
    capturedAt: z.iso.datetime(),
    snapshotId: z.uuid(),
  }),
  steps: z.array(restorePlanStepSchema).min(1),
  totals: z.object({
    bestEffort: z.int().nonnegative(),
    planned: z.int().nonnegative(),
    unavailable: z.int().nonnegative(),
  }),
  warnings: z.array(restorePlanWarningSchema),
});
export type RestorePreviewDraft = z.infer<typeof restorePreviewDraftSchema>;

export const restorePreviewSchema = restorePreviewDraftSchema.extend({
  duplicateRestore: z.object({
    priorRestoreCount: z.int().nonnegative(),
    requiresExplicitOverride: z.boolean(),
  }),
  planSha256: z.string().regex(/^[a-f0-9]{64}$/),
});
export type RestorePreview = z.infer<typeof restorePreviewSchema>;

export const selectiveRestorePreviewDraftSchema = z.object({
  archiveOnly: z.object({
    activities: z.int().nonnegative(),
    comments: z.int().nonnegative(),
  }),
  destination: z.object({
    boardId: z.string().min(1).max(512),
    kind: z.literal("original_board"),
  }),
  safety: z.object({
    confirmed: z.literal(false),
    createsOnly: z.literal(true),
    dryRun: z.literal(true),
    noOverwriteOrDelete: z.literal(true),
    trelloWrites: z.literal(0),
  }),
  schemaVersion: z.literal(1),
  selection: z.object({
    name: z.string().min(1),
    scope: selectiveRestoreScopeSchema,
    sourceId: z.string().min(1).max(512),
  }),
  snapshot: z.object({
    boardName: z.string().min(1),
    capturedAt: z.iso.datetime(),
    snapshotId: z.uuid(),
    sourceBoardId: z.string().min(1).max(512),
  }),
  steps: z.array(restorePlanStepSchema).min(1),
  totals: z.object({
    bestEffort: z.int().nonnegative(),
    planned: z.int().positive(),
    unavailable: z.int().nonnegative(),
  }),
  warnings: z.array(restorePlanWarningSchema),
});
export const selectiveRestorePreviewSchema =
  selectiveRestorePreviewDraftSchema.extend({
    planSha256: z.string().regex(/^[a-f0-9]{64}$/),
  });
export type SelectiveRestorePreview = z.infer<
  typeof selectiveRestorePreviewSchema
>;

export const selectiveRestoreConfirmationRequestSchema = z.object({
  acknowledgements: z.object({
    fidelityReviewed: z.literal(true),
    noOverwriteOrDelete: z.literal(true),
    restoreIntoOriginalBoard: z.literal(true),
  }),
  duplicatePolicy: z.literal("block"),
  idempotencyKey: z.uuid(),
  planSha256: z.string().regex(/^[a-f0-9]{64}$/),
  scope: selectiveRestoreScopeSchema,
  selectedSourceId: z.string().min(1).max(512),
  snapshotId: z.uuid(),
});
export type SelectiveRestoreConfirmationRequest = z.infer<
  typeof selectiveRestoreConfirmationRequestSchema
>;

const restoreAcknowledgementsSchema = z.object({
  createsNewPrivateBoard: z.literal(true),
  fidelityReviewed: z.literal(true),
  noOverwriteOrDelete: z.literal(true),
  sourceUnchanged: z.literal(true),
});

const restoreConfirmationBaseSchema = z.object({
  idempotencyKey: z.uuid(),
  planSha256: z.string().regex(/^[a-f0-9]{64}$/),
  snapshotId: z.uuid(),
});

export const restoreConfirmationRequestSchema = z.discriminatedUnion(
  "duplicatePolicy",
  [
    restoreConfirmationBaseSchema.extend({
      acknowledgements: restoreAcknowledgementsSchema,
      duplicatePolicy: z.literal("block"),
    }),
    restoreConfirmationBaseSchema.extend({
      acknowledgements: restoreAcknowledgementsSchema.extend({
        duplicateRestoreReviewed: z.literal(true),
      }),
      duplicatePolicy: z.literal("allow_explicit"),
    }),
  ],
);
export type RestoreConfirmationRequest = z.infer<
  typeof restoreConfirmationRequestSchema
>;

export const restorePreparationResponseSchema = z.object({
  dispatchStatus: z.enum(["disabled", "queued"]),
  jobId: z.uuid(),
  operationCount: z.int().positive(),
  planSha256: z.string().regex(/^[a-f0-9]{64}$/),
  status: z.literal("prepared"),
});
export type RestorePreparationResponse = z.infer<
  typeof restorePreparationResponseSchema
>;

export const restoreJobStatusSchema = z.enum([
  "pending",
  "running",
  "paused",
  "canceled",
  "complete",
  "complete_with_warnings",
  "failed",
]);
export type RestoreJobStatus = z.infer<typeof restoreJobStatusSchema>;

export const restoreObjectStatusSchema = z.enum([
  "pending",
  "running",
  "created",
  "skipped",
  "failed",
]);
export type RestoreObjectStatus = z.infer<typeof restoreObjectStatusSchema>;

const restoreResultCodeSchema = z.string().regex(/^[a-z0-9_]{1,80}$/);

export const cloudRestoreOutcomeCountsSchema = z.object({
  created: z.int().nonnegative(),
  failed: z.int().nonnegative(),
  pending: z.int().nonnegative(),
  running: z.int().nonnegative(),
  skipped: z.int().nonnegative(),
});
export type CloudRestoreOutcomeCounts = z.infer<
  typeof cloudRestoreOutcomeCountsSchema
>;

export const cloudRestoreSummarySchema = z.object({
  completedAt: z.iso.datetime().nullable(),
  controlState: z.enum([
    "active",
    "pause_requested",
    "paused",
    "cancel_requested",
    "canceled",
  ]),
  createdAt: z.iso.datetime(),
  destinationBoardId: z.string().min(1).max(512).nullable(),
  errorCode: restoreResultCodeSchema.nullable(),
  jobId: z.uuid(),
  mode: z.enum(["new_board", "selective"]),
  outcomes: cloudRestoreOutcomeCountsSchema,
  progress: z.object({
    completed: z.int().nonnegative(),
    total: z.int().nonnegative(),
  }),
  scope: z.enum(["all", "list", "card"]),
  source: z.enum(["cloud_backup", "uploaded_zip"]),
  snapshotId: z.uuid(),
  startedAt: z.iso.datetime().nullable(),
  status: restoreJobStatusSchema,
});
export type CloudRestoreSummary = z.infer<typeof cloudRestoreSummarySchema>;

/**
 * Restore reports are customer-visible for this many days. The underlying
 * operational, audit, and provenance records can outlive this presentation
 * window when they are required for safe resumability or duplicate guards.
 */
export const CLOUD_RESTORE_REPORT_VISIBILITY_DAYS = 30;

export const cloudRestoreHistorySchema = z.object({
  restores: z.array(cloudRestoreSummarySchema).max(20),
});
export type CloudRestoreHistory = z.infer<typeof cloudRestoreHistorySchema>;

export const cloudRestoreOperationReportSchema = z.object({
  attemptCount: z.int().min(0).max(10),
  completedAt: z.iso.datetime().nullable(),
  destinationId: z.string().min(1).max(512).nullable(),
  operationType: restoreStepTypeSchema,
  resultCode: restoreResultCodeSchema.nullable(),
  sequence: z.int().positive(),
  sourceId: z.string().min(1).max(512),
  status: restoreObjectStatusSchema,
});
export type CloudRestoreOperationReport = z.infer<
  typeof cloudRestoreOperationReportSchema
>;

export const cloudRestoreReportSchema = cloudRestoreSummarySchema.extend({
  nextAfter: z.int().positive().nullable(),
  operations: z.array(cloudRestoreOperationReportSchema).max(100),
});
export type CloudRestoreReport = z.infer<typeof cloudRestoreReportSchema>;

export interface CreateRestorePreviewOptions {
  attachmentStatuses?: Readonly<Record<string, RestoreAttachmentStatus>>;
}

export interface SelectiveRestoreSelection {
  scope: SelectiveRestoreScope;
  selectedSourceId: string;
}

function byPositionAndId(
  left: { id: string; position: number },
  right: { id: string; position: number },
): number {
  return left.position - right.position || left.id.localeCompare(right.id);
}

function hasActiveCardCover(
  card: PortableBoardSnapshotV1["cards"][number],
): boolean {
  return (
    card.cover !== null &&
    (card.cover.attachmentId !== null ||
      card.cover.color !== null ||
      card.cover.uploadedBackgroundReference !== null)
  );
}

function restoresCoverWithAttachment(
  card: PortableBoardSnapshotV1["cards"][number],
  statuses: Readonly<Record<string, RestoreAttachmentStatus>>,
): boolean {
  const attachmentId = card.cover?.attachmentId;
  return (
    attachmentId !== null &&
    attachmentId !== undefined &&
    ["copied", "link_only"].includes(statuses[attachmentId] ?? "") &&
    ["dark", "light"].includes(card.cover!.brightness) &&
    ["full", "normal"].includes(card.cover!.size)
  );
}

export function restoreOperationKey(
  operation: Pick<RestoreExecutionOperation, "operationType" | "sourceId">,
): string {
  return `${operation.operationType}:${operation.sourceId}`;
}

export function createRestoreOperations(
  snapshot: PortableBoardSnapshotV1,
  options: CreateRestorePreviewOptions = {},
): RestoreExecutionOperation[] {
  const statuses = options.attachmentStatuses ?? {};
  const operations: RestoreExecutionOperation[] = [];
  const add = (
    operationType: RestoreStepType,
    sourceId: string,
    parentSourceId: string | null,
    fidelity: RestoreFidelity,
  ): void => {
    operations.push({
      fidelity,
      operationType,
      parentSourceId,
      sequence: operations.length + 1,
      sourceId,
    });
  };
  const lists = snapshot.lists.toSorted(byPositionAndId);
  const listRank = new Map(lists.map(({ id }, index) => [id, index]));
  const cards = snapshot.cards.toSorted(
    (left, right) =>
      (listRank.get(left.listId) ?? Number.MAX_SAFE_INTEGER) -
        (listRank.get(right.listId) ?? Number.MAX_SAFE_INTEGER) ||
      byPositionAndId(left, right),
  );
  const cardRank = new Map(cards.map(({ id }, index) => [id, index]));
  const checklists = snapshot.checklists.toSorted(
    (left, right) =>
      (cardRank.get(left.cardId) ?? Number.MAX_SAFE_INTEGER) -
        (cardRank.get(right.cardId) ?? Number.MAX_SAFE_INTEGER) ||
      byPositionAndId(left, right),
  );

  add("board", snapshot.board.id, null, "best_effort");
  for (const list of lists) {
    add("list", list.id, snapshot.board.id, "supported");
  }
  for (const label of snapshot.labels.toSorted((left, right) =>
    left.id.localeCompare(right.id),
  )) {
    add("label", label.id, snapshot.board.id, "supported");
  }
  for (const field of snapshot.customFields.toSorted(byPositionAndId)) {
    add(
      "custom_field",
      `definition:${field.id}`,
      snapshot.board.id,
      "best_effort",
    );
  }
  for (const card of cards) {
    add("card", card.id, card.listId, "supported");
  }
  for (const card of cards) {
    for (const memberId of card.memberIds.toSorted()) {
      add(
        "member_assignment",
        `card:${card.id}:member:${memberId}`,
        card.id,
        "best_effort",
      );
    }
  }
  for (const card of cards) {
    for (const item of card.customFieldItems.toSorted((left, right) =>
      left.id.localeCompare(right.id),
    )) {
      add("custom_field", `value:${item.id}`, card.id, "best_effort");
    }
  }
  for (const checklist of checklists) {
    add("checklist", checklist.id, checklist.cardId, "supported");
  }
  for (const checklist of checklists) {
    for (const item of checklist.items.toSorted(byPositionAndId)) {
      add("check_item", item.id, checklist.id, "supported");
    }
  }
  for (const checklist of checklists) {
    for (const item of checklist.items.toSorted(byPositionAndId)) {
      if (item.memberId !== null) {
        add(
          "member_assignment",
          `check_item:${item.id}:member:${item.memberId}`,
          item.id,
          "best_effort",
        );
      }
    }
  }
  for (const card of cards) {
    if (
      hasActiveCardCover(card) &&
      !restoresCoverWithAttachment(card, statuses)
    ) {
      add("card_cover", card.id, card.id, "best_effort");
    }
  }
  const restorableCardIds = new Set(cards.map(({ id }) => id));
  const historicalRecords = [
    ...snapshot.comments
      .filter(({ cardId }) => restorableCardIds.has(cardId))
      .map((comment) => ({
        cardId: comment.cardId,
        createdAt: comment.createdAt,
        id: comment.id,
        type: "historical_comment" as const,
      })),
    ...snapshot.activities
      .filter(
        ({ cardId, type }) =>
          type !== "commentCard" && restorableCardIds.has(cardId),
      )
      .map((activity) => ({
        cardId: activity.cardId,
        createdAt: activity.createdAt,
        id: activity.id,
        type: "historical_activity" as const,
      })),
  ].toSorted(
    (left, right) =>
      left.createdAt.localeCompare(right.createdAt) ||
      left.type.localeCompare(right.type) ||
      left.id.localeCompare(right.id),
  );
  for (const record of historicalRecords) {
    add(record.type, record.id, record.cardId, "best_effort");
  }
  // File transfer is deliberately the final restore phase. Attachment-backed
  // covers are applied as part of their attachment operation so no later
  // customer-data operation can be blocked by a large upload.
  for (const attachment of snapshot.attachments.toSorted(
    (left, right) =>
      Number(statuses[left.id] === "copied") -
        Number(statuses[right.id] === "copied") ||
      (cardRank.get(left.cardId) ?? Number.MAX_SAFE_INTEGER) -
        (cardRank.get(right.cardId) ?? Number.MAX_SAFE_INTEGER) ||
      left.id.localeCompare(right.id),
  )) {
    const status = statuses[attachment.id];
    if (status === "copied") {
      add("attachment_file", attachment.id, attachment.cardId, "best_effort");
    } else if (status === "link_only") {
      add("attachment_link", attachment.id, attachment.cardId, "best_effort");
    }
  }
  return z.array(restoreExecutionOperationSchema).parse(operations);
}

export function createSelectiveRestoreOperations(
  snapshot: PortableBoardSnapshotV1,
  selection: SelectiveRestoreSelection,
  options: CreateRestorePreviewOptions = {},
): RestoreExecutionOperation[] {
  const selectedList =
    selection.scope === "list"
      ? snapshot.lists.find(({ id }) => id === selection.selectedSourceId)
      : undefined;
  const selectedCard =
    selection.scope === "card"
      ? snapshot.cards.find(({ id }) => id === selection.selectedSourceId)
      : undefined;
  if (
    (selection.scope === "list" && !selectedList) ||
    (selection.scope === "card" && !selectedCard)
  ) {
    throw new Error("The selective restore object is not in this snapshot.");
  }
  const cards = snapshot.cards
    .filter((card) =>
      selectedCard
        ? card.id === selectedCard.id
        : card.listId === selectedList!.id,
    )
    .toSorted(byPositionAndId);
  const cardIds = new Set(cards.map(({ id }) => id));
  const checklists = snapshot.checklists
    .filter(({ cardId }) => cardIds.has(cardId))
    .toSorted(byPositionAndId);
  const statuses = options.attachmentStatuses ?? {};
  const operations: RestoreExecutionOperation[] = [];
  const add = (
    operationType: RestoreStepType,
    sourceId: string,
    parentSourceId: string | null,
    fidelity: RestoreFidelity,
  ): void => {
    operations.push({
      fidelity,
      operationType,
      parentSourceId,
      sequence: operations.length + 1,
      sourceId,
    });
  };
  if (selectedList) {
    add("list", selectedList.id, snapshot.board.id, "supported");
  }
  const selectedLabelIds = new Set(cards.flatMap(({ labelIds }) => labelIds));
  for (const label of snapshot.labels
    .filter(({ id }) => selectedLabelIds.has(id))
    .toSorted((left, right) => left.id.localeCompare(right.id))) {
    add("label", label.id, snapshot.board.id, "best_effort");
  }
  const selectedFieldIds = new Set(
    cards.flatMap((card) =>
      card.customFieldItems.map(({ customFieldId }) => customFieldId),
    ),
  );
  for (const field of snapshot.customFields
    .filter(({ id }) => selectedFieldIds.has(id))
    .toSorted(byPositionAndId)) {
    add(
      "custom_field",
      `definition:${field.id}`,
      snapshot.board.id,
      "best_effort",
    );
  }
  for (const card of cards) {
    add("card", card.id, card.listId, "supported");
  }
  for (const card of cards) {
    for (const memberId of card.memberIds.toSorted()) {
      add(
        "member_assignment",
        `card:${card.id}:member:${memberId}`,
        card.id,
        "best_effort",
      );
    }
    for (const item of card.customFieldItems.toSorted((left, right) =>
      left.id.localeCompare(right.id),
    )) {
      add("custom_field", `value:${item.id}`, card.id, "best_effort");
    }
  }
  for (const checklist of checklists) {
    add("checklist", checklist.id, checklist.cardId, "supported");
    for (const item of checklist.items.toSorted(byPositionAndId)) {
      add("check_item", item.id, checklist.id, "supported");
      if (item.memberId !== null) {
        add(
          "member_assignment",
          `check_item:${item.id}:member:${item.memberId}`,
          item.id,
          "best_effort",
        );
      }
    }
  }
  for (const card of cards) {
    if (
      hasActiveCardCover(card) &&
      !restoresCoverWithAttachment(card, statuses)
    ) {
      add("card_cover", card.id, card.id, "best_effort");
    }
  }
  const historicalRecords = [
    ...snapshot.comments
      .filter(({ cardId }) => cardIds.has(cardId))
      .map((comment) => ({
        cardId: comment.cardId,
        createdAt: comment.createdAt,
        id: comment.id,
        type: "historical_comment" as const,
      })),
    ...snapshot.activities
      .filter(
        ({ cardId, type }) => type !== "commentCard" && cardIds.has(cardId),
      )
      .map((activity) => ({
        cardId: activity.cardId,
        createdAt: activity.createdAt,
        id: activity.id,
        type: "historical_activity" as const,
      })),
  ].toSorted(
    (left, right) =>
      left.createdAt.localeCompare(right.createdAt) ||
      left.type.localeCompare(right.type) ||
      left.id.localeCompare(right.id),
  );
  for (const record of historicalRecords) {
    add(record.type, record.id, record.cardId, "best_effort");
  }
  for (const attachment of snapshot.attachments
    .filter(({ cardId }) => cardIds.has(cardId))
    .toSorted(
      (left, right) =>
        Number(statuses[left.id] === "copied") -
          Number(statuses[right.id] === "copied") ||
        left.id.localeCompare(right.id),
    )) {
    if (statuses[attachment.id] === "copied") {
      add("attachment_file", attachment.id, attachment.cardId, "best_effort");
    } else if (statuses[attachment.id] === "link_only") {
      add("attachment_link", attachment.id, attachment.cardId, "best_effort");
    }
  }
  return z.array(restoreExecutionOperationSchema).parse(operations);
}

export function createSelectiveRestorePreview(
  snapshot: PortableBoardSnapshotV1,
  selection: SelectiveRestoreSelection,
  options: CreateRestorePreviewOptions = {},
): z.infer<typeof selectiveRestorePreviewDraftSchema> {
  const operations = createSelectiveRestoreOperations(
    snapshot,
    selection,
    options,
  );
  const selected =
    selection.scope === "card"
      ? snapshot.cards.find(({ id }) => id === selection.selectedSourceId)!
      : snapshot.lists.find(({ id }) => id === selection.selectedSourceId)!;
  const count = (type: RestoreStepType): number =>
    operations.filter(({ operationType }) => operationType === type).length;
  const selectedCardIds = new Set(
    snapshot.cards
      .filter((card) =>
        selection.scope === "card"
          ? card.id === selection.selectedSourceId
          : card.listId === selection.selectedSourceId,
      )
      .map(({ id }) => id),
  );
  const unavailable = snapshot.attachments.filter(
    ({ cardId, id }) =>
      selectedCardIds.has(cardId) &&
      !["copied", "link_only"].includes(options.attachmentStatuses?.[id] ?? ""),
  ).length;
  const stepTypes: RestoreStepType[] = [
    "list",
    "label",
    "custom_field",
    "card",
    "member_assignment",
    "checklist",
    "check_item",
    "card_cover",
    "historical_comment",
    "historical_activity",
    "attachment_file",
    "attachment_link",
  ];
  const steps = stepTypes
    .flatMap((type, index) => {
      const operationCount = count(type);
      return operationCount === 0
        ? []
        : [
            {
              count: operationCount,
              fidelity: operations.some(
                (operation) =>
                  operation.operationType === type &&
                  operation.fidelity === "best_effort",
              )
                ? ("best_effort" as const)
                : ("supported" as const),
              order: index + 1,
              type,
            },
          ];
    })
    .map((step, index) => ({ ...step, order: index + 1 }));
  const warnings = [
    warning(
      "ORIGINAL_IDENTITY_NOT_PRESERVED",
      1,
      "Selective restore creates new Trello IDs and audit timestamps on the original board.",
    ),
    warning(
      "CUSTOM_FIELDS_BEST_EFFORT",
      count("custom_field"),
      "Custom-field values require the captured definition to remain compatible on the original board.",
    ),
    warning(
      "MEMBERS_REQUIRE_MAPPING",
      count("member_assignment"),
      "Assignments are applied only when the same member still belongs to the original board.",
    ),
    warning(
      "ATTACHMENTS_UNAVAILABLE",
      unavailable,
      "Attachment files not retained by Cloud Preserve cannot be re-uploaded.",
    ),
    warning(
      "CARD_COVERS_BEST_EFFORT",
      snapshot.cards.filter(
        (card) => selectedCardIds.has(card.id) && hasActiveCardCover(card),
      ).length,
      "Attachment covers require a successful selective re-upload and provider photos may remain metadata-only.",
    ),
    warning(
      "HISTORICAL_RECORDS_BEST_EFFORT",
      count("historical_comment") + count("historical_activity"),
      "Captured comments and non-comment activity are added as clearly labelled historical-record comments. Trello attributes these representations to the restoring administrator at restore time; the labels retain the captured actor, time, and source type.",
    ),
  ].filter((item): item is RestorePlanWarning => item !== null);
  return selectiveRestorePreviewDraftSchema.parse({
    archiveOnly: {
      activities: snapshot.activities.filter(({ cardId }) =>
        selectedCardIds.has(cardId),
      ).length,
      comments: snapshot.comments.filter(({ cardId }) =>
        selectedCardIds.has(cardId),
      ).length,
    },
    destination: { boardId: snapshot.source.boardId, kind: "original_board" },
    safety: {
      confirmed: false,
      createsOnly: true,
      dryRun: true,
      noOverwriteOrDelete: true,
      trelloWrites: 0,
    },
    schemaVersion: 1,
    selection: {
      name: selected.name,
      scope: selection.scope,
      sourceId: selection.selectedSourceId,
    },
    snapshot: {
      boardName: snapshot.board.name,
      capturedAt: snapshot.capturedAt,
      snapshotId: snapshot.snapshotId,
      sourceBoardId: snapshot.source.boardId,
    },
    steps,
    totals: {
      bestEffort: operations.filter(
        ({ fidelity }) => fidelity === "best_effort",
      ).length,
      planned: operations.length,
      unavailable,
    },
    warnings,
  });
}

export function remainingRestoreOperations(
  operations: readonly RestoreExecutionOperation[],
  completedOperationKeys: ReadonlySet<string>,
): RestoreExecutionOperation[] {
  return operations.filter(
    (operation) => !completedOperationKeys.has(restoreOperationKey(operation)),
  );
}

function warning(
  code: RestoreWarningCode,
  count: number,
  message: string,
): RestorePlanWarning | null {
  return count > 0 ? { code, count, message } : null;
}

export function createRestorePreview(
  snapshot: PortableBoardSnapshotV1,
  options: CreateRestorePreviewOptions = {},
): RestorePreviewDraft {
  const operations = createRestoreOperations(snapshot, options);
  const countOperations = (type: RestoreStepType): number =>
    operations.filter(({ operationType }) => operationType === type).length;
  const copiedAttachments = countOperations("attachment_file");
  const linkedAttachments = countOperations("attachment_link");
  const unavailableAttachments =
    snapshot.attachments.length - copiedAttachments - linkedAttachments;
  const memberAssignments = countOperations("member_assignment");
  const checkItems = countOperations("check_item");
  const cardCovers = snapshot.cards.filter(hasActiveCardCover).length;
  const historicalComments = countOperations("historical_comment");
  const historicalActivities = countOperations("historical_activity");
  const customFieldOperations = countOperations("custom_field");
  const archivedObjects =
    snapshot.lists.filter(({ closed }) => closed).length +
    snapshot.cards.filter(({ closed }) => closed).length;
  const personalState =
    (snapshot.board.subscribed ? 1 : 0) +
    snapshot.cards.filter(({ subscribed }) => subscribed).length +
    snapshot.cards.reduce(
      (count, card) => count + card.voterMemberIds.length,
      0,
    );
  const steps: RestorePlanStep[] = [
    { count: 1, fidelity: "best_effort", order: 1, type: "board" },
    {
      count: snapshot.lists.length,
      fidelity: "supported",
      order: 2,
      type: "list",
    },
    {
      count: snapshot.labels.length,
      fidelity: "supported",
      order: 3,
      type: "label",
    },
    {
      count: customFieldOperations,
      fidelity: "best_effort",
      order: 4,
      type: "custom_field",
    },
    {
      count: snapshot.cards.length,
      fidelity: "supported",
      order: 5,
      type: "card",
    },
    {
      count: memberAssignments,
      fidelity: "best_effort",
      order: 6,
      type: "member_assignment",
    },
    {
      count: snapshot.checklists.length,
      fidelity: "supported",
      order: 7,
      type: "checklist",
    },
    {
      count: checkItems,
      fidelity: "supported",
      order: 8,
      type: "check_item",
    },
    {
      count: countOperations("card_cover"),
      fidelity: "best_effort",
      order: 9,
      type: "card_cover",
    },
    {
      count: historicalComments,
      fidelity: "best_effort",
      order: 10,
      type: "historical_comment",
    },
    {
      count: historicalActivities,
      fidelity: "best_effort",
      order: 11,
      type: "historical_activity",
    },
    {
      count: linkedAttachments,
      fidelity: "best_effort",
      order: 12,
      type: "attachment_link",
    },
    {
      count: copiedAttachments,
      fidelity: "best_effort",
      order: 13,
      type: "attachment_file",
    },
  ];
  const warnings = [
    warning(
      "ORIGINAL_IDENTITY_NOT_PRESERVED",
      1,
      "The connected Trello administrator creates the recovery objects. Trello records new IDs, current timestamps, that administrator as the actor, and new restore-generated activity.",
    ),
    warning(
      "HISTORICAL_RECORDS_BEST_EFFORT",
      historicalComments + historicalActivities,
      "Captured comments and non-comment activity are added as clearly labelled historical-record comments. Trello attributes these representations to the restoring administrator at restore time; the labels retain the captured actor, time, and source type.",
    ),
    warning(
      "CUSTOM_FIELDS_BEST_EFFORT",
      customFieldOperations,
      "Custom fields and values require compatible destination permissions and may be skipped with an exact result.",
    ),
    warning(
      "MEMBERS_REQUIRE_MAPPING",
      memberAssignments,
      "Assignments are applied only when the same Trello member is available on the destination board.",
    ),
    warning(
      "MEMBERSHIPS_BEST_EFFORT",
      snapshot.memberships.length,
      "Board memberships and roles are reviewed separately and missing members are never substituted.",
    ),
    warning(
      "ATTACHMENTS_UNAVAILABLE",
      unavailableAttachments,
      "Attachment files not retained by Cloud Preserve cannot be re-uploaded; their metadata remains in the archive.",
    ),
    warning(
      "CARD_COVERS_BEST_EFFORT",
      cardCovers,
      "Colour covers are recreated where supported. Attachment covers require a retained file and successful re-upload. Trello-hosted provider photos, including Unsplash covers, use non-portable background identifiers and remain metadata-only.",
    ),
    warning(
      "BOARD_BACKGROUND_NOT_RESTORED",
      [
        snapshot.board.preferences.background,
        snapshot.board.preferences.backgroundBottomColor,
        snapshot.board.preferences.backgroundBrightness,
        snapshot.board.preferences.backgroundTopColor,
      ].some((value) => value !== null) ||
        snapshot.board.preferences.backgroundTile !== null
        ? 1
        : 0,
      "The captured board background, image, and colour metadata remains in the archive. Trello board backgrounds are not reproduced unless a future natively portable representation is available.",
    ),
    warning(
      "ARCHIVED_OBJECTS_RECREATED",
      archivedObjects,
      "Archived lists and cards are created first and archived only after their dependencies are restored.",
    ),
    warning(
      "PERSONAL_STATE_NOT_RESTORED",
      personalState,
      "Votes, watchers, and personal subscription state are not restored.",
    ),
  ].filter((item): item is RestorePlanWarning => item !== null);
  const bestEffort = steps
    .filter(({ fidelity }) => fidelity === "best_effort")
    .reduce((count, step) => count + step.count, 0);
  return restorePreviewDraftSchema.parse({
    archiveOnly: {
      activities: snapshot.activities.length,
      comments: snapshot.comments.length,
    },
    destination: {
      kind: "new_private_board",
      permissionLevel: "private",
    },
    safety: {
      confirmed: false,
      createsOnly: true,
      dryRun: true,
      sourceUnchanged: true,
      trelloWrites: 0,
    },
    schemaVersion: 1,
    snapshot: {
      boardName: snapshot.board.name,
      capturedAt: snapshot.capturedAt,
      snapshotId: snapshot.snapshotId,
    },
    steps,
    totals: {
      bestEffort,
      planned: steps.reduce((count, step) => count + step.count, 0),
      unavailable: unavailableAttachments,
    },
    warnings,
  });
}
